import React, {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import * as Clipboard from 'expo-clipboard';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Moment from 'moment';
import {
  View, Text, StyleSheet, ScrollView, Image, Pressable, ActivityIndicator, KeyboardAvoidingView,
} from 'react-native';
import * as Animatable from 'react-native-animatable';
import Modal from 'react-native-modal';
import { useNavigation } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import SendMessageModal from '../../../Tickets/components/SendMessageModal';
import TicketActivity from './components/TicketActivity';
import CommentsPane from '../../../Tickets/components/CommentsPane';
import ScreenEmptyState from '../../../../components/ScreenEmptyState';
import NoSelectedTicketImage from '../../../Tickets/components/NoSelectedTicketImage';
import Colors from '../../../../constants/Colors';
import Style from '../../../../constants/Style';
import ClosureModal from '../../../Tickets/components/ClosureModal';
import PressableStyled from '../../../../components/PressableStyled';
import ProfileIcon from '../../../../components/24pxIcons/ProfileIcon';
import isMobile from '../../../../components/IsMobile';
import TicketPrioritySelectorRow from './TicketPrioritySelectorRow';
import { logEvent } from '../../../../components/LogEvent';
import ZoomIcon from '../../../../components/24pxIcons/ZoomIcon';
import {
  ActivitySection, AssetInstance,
  DEFAULT_TICKET_PRIORITY, Ticket, ticketPriorityIsValid, TicketStatus, UserGroup,
  UserInstance, UserPermission,
} from '../../../../constants/Types';
import AssetIcon from '../../../../components/AssetIcon';
import TicketAssigneeIcon from '../../../Tickets/components/TicketAssigneeIcon';
import TicketStatusButtonsRow from './TicketStatusButtonsRow';
import { AppScreen } from '../../../../navigation/LinkingConfiguration';
import TicketAssignee from '../../../Tickets/components/TicketAssignee';
import MessageReporterPane from '../../../Tickets/components/MessageReporterPane';
import TicketBrief from '../../../Tickets/components/TicketBrief';
import TicketUserGroups from '../../../Tickets/components/TicketUserGroups';
import MyGroupsIcon from '../../../../components/24pxIcons/MyGroupsIcon';
import CloseIcon16 from '../../../../components/16pxIcons/CloseIcon16';
import EllipsisIcon from '../../../../components/EllipsisIcon';
import { BuildingSettingsPriority, pad } from '../../../LocationManagement/components/BuildingSettingsModal/data';
import { Building } from '../../../LocationManagement/components/AddBuildingModal';
import { updateSelectedTicket } from '../../../../redux/selectedTicketSlice';
import LinkChainIcon24 from '../../../../components/24pxIcons/LinkChainIcon24';

interface selectedTicketWebProps {
  selectedTicket: Ticket,
  users: UserInstance[],
  renderSharePane,
  moreOptionsVisible: boolean,
  renderMoreOptions,
  selectedBuilding: string,
  imageModalVisible: boolean,
  setImageModalVisible: Dispatch<SetStateAction<boolean>>,
  ticketActivityRef,
  name: string,
  closureModalVisible: boolean,
  setClosureModalVisible: Dispatch<SetStateAction<boolean>>,
  resolve: () => void,
  showSpinner: boolean,
  hide?: () => void,
  setLoading: Dispatch<SetStateAction<boolean>>,
  setShowSpinner: Dispatch<SetStateAction<boolean>>,
  showAlert,
  userGroups: UserGroup[],
  asset: AssetInstance | null,
  updateSelectedTicketInList,
  onDeleteTicket?: () => void,
}

const SelectedTicket = ({
  selectedTicket, users, renderSharePane, moreOptionsVisible, resolve, showAlert, asset, setLoading,
  renderMoreOptions, imageModalVisible, setImageModalVisible, name, setShowSpinner, userGroups,
  selectedBuilding,
  showSpinner, closureModalVisible, setClosureModalVisible, hide, ticketActivityRef,
  updateSelectedTicketInList, onDeleteTicket,
}: selectedTicketWebProps) => {
  const dispatch = useDispatch();
  const setSelectedTicket = (ticket: Ticket | null) => dispatch(updateSelectedTicket(ticket));
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canUpdateTickets: boolean = isAdmin || permissions.includes(UserPermission.TICKETS_UPDATE);
  const canViewAssets: boolean = isAdmin || permissions.includes(UserPermission.ASSETS_READ);
  const canDeleteTickets: boolean = isAdmin || permissions.includes(UserPermission.TICKETS_DELETE);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState<boolean>(false);
  const [messageModalShown, setMessageModalShown] = useState<boolean>(false);
  const [activityLogModalVisible, setActivityLogModalVisible] = useState<boolean>(false);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState<boolean>(false);
  const [showHeaderMenu, setShowHeaderMenu] = useState<boolean>(false);
  const [isAssetLinkHovered, setIsAssetLinkHovered] = useState<boolean>(false);
  const [isLocationLinkHovered, setIsLocationLinkHovered] = useState<boolean>(false);
  const [isTicketLinkHovered, setIsTicketLinkHovered] = useState<boolean>(false);
  const [clientName, setClientName] = useState<string>('');
  const [assigneeSearchValue, setAssigneeSearchValue] = useState<string>('');
  const [activeTab, setActiveTab] = useState<ActivitySection>(ActivitySection.ALL);
  const [savingTicketUserGroups, setSavingTicketUserGroups] = useState<boolean>(false);
  const [time, setTime] = useState<number>(Date.now());
  const navigation = useNavigation();
  const scrollRef = React.useRef<ScrollView>(null);
  const isDev: boolean = useSelector((state:
    { devState: { isDev: boolean } }) => state.devState.isDev);
  const slaHours: BuildingSettingsPriority[] = useSelector((state:
    { selectedBuilding: Building }) => state.selectedBuilding.SlaHours);

  useEffect(() => {
    setShowPriorityDropdown(false);
    setShowAssigneeDropdown(false);
    setShowHeaderMenu(false);
    setActivityLogModalVisible(false);
  }, [selectedTicket]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    AsyncStorage.getItem('clientName').then((storedClientName) => setClientName(storedClientName ?? ''));
  }, []);

  if (selectedTicket) {
    let reporterName: string | null = selectedTicket.reporterName?.trim();
    if (!reporterName) {
      if (selectedTicket.reporter) reporterName = selectedTicket.reporter?.trim();
      else if (selectedTicket.reporterEmail) reporterName = selectedTicket.reporterEmail?.trim();
    }
    let { priority } = selectedTicket;
    if (!ticketPriorityIsValid(priority)) priority = selectedTicket.priority;
    if (!ticketPriorityIsValid(priority)) priority = DEFAULT_TICKET_PRIORITY;
    const { id } = selectedTicket;
    const uri: string = selectedTicket.images[0]?.filename;
    // const uri: string = selectedTicket.images[0]?.filename
    //   ?.replace(/[~`!@#$%^&*()+={};'"<>,?_]/g, ''); // TODO: do we need to strip these chars?
    const locationParts: string[] = [];
    if (selectedTicket.floorName) {
      locationParts.push(selectedTicket.floorName);
    }
    if (selectedTicket.locationName && selectedTicket.locationName !== selectedTicket.floorName) {
      locationParts.push(selectedTicket.locationName);
    }

    const navigateToAsset: () => void = () => {
      logEvent('SelectedTicket', 'go_to_asset');
      if (isMobile && hide) hide();
      const { assetId } = selectedTicket;
      if (!assetId) {
        showAlert('Asset unavailable', 'This ticket does not have an associated asset.');
        return;
      }
      if (isMobile) {
        navigation.navigate('AssetStack' as any, {
          assetId: selectedTicket.assetId ?? '-',
        });
      } else {
        navigation.navigate(AppScreen.ASSET_MANAGEMENT as any, { assetId });
        // TODO: remove following block after testing
        // navigation.navigate(AppScreen.TEMPLATE_MANAGEMENT as any, {
        //   assetId: selectedTicket.assetId ?? '-',
        //   templateId: selectedTicket.assetTemplateId,
        // });
      }
    };

    const copyTicketLink: () => void = () => {
      logEvent('SelectedTicket', 'copy_ticket_link');
      const subDomain = isDev ? 'qainsights' : 'insights';
      const ticketLink = `https://${subDomain}.hereworks.tech/Tickets?clientId=${encodeURIComponent(clientName)}&reportId=${encodeURIComponent(selectedTicket.id)}&buildingId=${encodeURIComponent(selectedBuilding ?? '')}`;
      Clipboard.setStringAsync(ticketLink);
      showAlert('Link copied', 'Ticket link copied to clipboard', false);
    };

    const shareFromHeaderMenu: () => void = () => {
      setShowHeaderMenu(false);
      copyTicketLink();
    };

    const showActivityLogFromHeaderMenu: () => void = () => {
      setShowHeaderMenu(false);
      setActivityLogModalVisible(true);
    };

    const deleteFromHeaderMenu: () => void = () => {
      setShowHeaderMenu(false);
      onDeleteTicket?.();
    };

    const isLocationSet: boolean = asset?.customFields.some(
      (field) => field.name === 'GeoX' && Number(field.value) > 0,
    ) || selectedTicket.onMap;

    const navigateToLocation: () => void = () => {
      logEvent('SelectedTicket', 'go_to_location');
      const onMap = isLocationSet;
      if (!onMap && !canUpdateTickets) {
        showAlert('Location not set', 'This asset is not placed on the map.');
        return;
      }
      if (!onMap && isMobile) {
        showAlert('Location not set', 'This asset is not placed on the map. Please use a desktop to set the location.');
        return;
      }
      if (isMobile && hide) hide();
      if (isMobile) {
        navigation.navigate(AppScreen.MAP as any, {
          floor: selectedTicket.floorName,
          assetId: selectedTicket.assetId,
          ticketId: selectedTicket.id,
          ticketsView: true,
          ticketDate: selectedTicket.createdOn,
        });
      } else {
        const {
          areaId, spaceId, assetId, id: ticketId, createdOn: ticketDate,
        } = selectedTicket;
        navigation.navigate(AppScreen.MAP as any, {
          floor: selectedTicket.floorName,
          areaId,
          spaceId,
          assetId,
          ticketId,
          ticketsView: onMap,
          ticketDate,
          placeAsset: !onMap && canUpdateTickets,
        });
      }
    };

    const formatDuration = (durationMs: number): string => {
      if (!durationMs || durationMs <= 0) return '-';
      const totalSeconds = Math.floor(durationMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      const padValue = (value: number): string => `${value}`.padStart(2, '0');
      return `${padValue(hours)}:${padValue(minutes)}:${padValue(seconds)}`;
    };

    const formatDueIn = (dueDate?: string): string => {
      if (!dueDate) return '-';
      const dueDateMs = new Date(dueDate).getTime();
      const deltaMs = dueDateMs - time;
      if (deltaMs >= 0) return `due in ${formatDuration(deltaMs)}`;
      return `overdue by ${formatDuration(Math.abs(deltaMs))}`;
    };

    const getDueStatusColor = (dueDate?: string): string => {
      if (!dueDate) return Colors.light.hereworksBlack500;
      const dueDateMs = new Date(dueDate).getTime();
      if (Number.isNaN(dueDateMs)) return Colors.light.hereworksBlack500;
      return dueDateMs - time >= 0
        ? Colors.light.hereworksGreen500
        : Colors.light.hereworksRed500;
    };

    const createdOnMs: number = selectedTicket.createdOn
      ? new Date(selectedTicket.createdOn).getTime() : 0;
    const startedOnMs: number = selectedTicket.startedOn
      ? new Date(selectedTicket.initialStartedOn ?? selectedTicket.startedOn).getTime() : 0;
    const completedOnMs: number = selectedTicket.completedOn
      ? new Date(selectedTicket.completedOn).getTime() : 0;
    const timeElapsedMs: number = (selectedTicket.completedOn ? completedOnMs : time) - createdOnMs;
    const timeOnTaskMs: number = selectedTicket.startedOn
      ? (selectedTicket.completedOn ? completedOnMs : time) - startedOnMs
      : 0;
    const actualResponseTimeMs: number = selectedTicket.startedOn ? startedOnMs - createdOnMs : 0;
    const resolutionHours: number | null = selectedTicket.slaHours
      ?? (ticketPriorityIsValid(priority) ? slaHours[priority]?.ResolutionHours : null);
    const resolutionMinutes: number | null = selectedTicket.slaMinutes
      ?? (ticketPriorityIsValid(priority) ? slaHours[priority]?.ResolutionMinutes : null);
    const slaResponseHours: number | null = ticketPriorityIsValid(priority)
      ? slaHours[priority]?.ResponseHours : null;
    const slaResponseMinutes: number | null = ticketPriorityIsValid(priority)
      ? slaHours[priority]?.ResponseMinutes : null;
    const responseTarget: string = (slaResponseHours || slaResponseMinutes)
      ? `${pad(slaResponseHours)}:${pad(slaResponseMinutes)}:00` : '-';
    const resolutionTarget: string = (resolutionHours || resolutionMinutes)
      ? `${pad(resolutionHours)}:${pad(resolutionMinutes ?? 0)}:00` : '-';
    const responseActual: string = formatDuration(actualResponseTimeMs);
    const resolutionActual: string = formatDuration(timeElapsedMs);
    const responseTime: string = selectedTicket.completedOn
      ? `Target: ${responseTarget} | Actual: ${responseActual}`
      : formatDueIn(selectedTicket.responseDueDate);
    const resolutionTime: string = selectedTicket.completedOn
      ? `Target: ${resolutionTarget} | Actual: ${resolutionActual}`
      : formatDueIn(selectedTicket.dueDate);
    const responseTimeColor: string = selectedTicket.completedOn
      ? Colors.light.hereworksBlack500
      : getDueStatusColor(selectedTicket.responseDueDate);
    const resolutionTimeColor: string = selectedTicket.completedOn
      ? Colors.light.hereworksBlack500
      : getDueStatusColor(selectedTicket.dueDate);
    const timeOnTask: string = formatDuration(timeOnTaskMs);
    const startedOn: string = selectedTicket.startedOn
      ? Moment(new Date(selectedTicket.startedOn)).format('llll') : '-';
    const createdOn: string = selectedTicket.createdOn
      ? Moment(new Date(selectedTicket.createdOn)).format('llll') : '-';

    const getStatusTagStyles = () => {
      if (selectedTicket.status === TicketStatus.COMPLETED) {
        return {
          borderColor: Colors.light.hereworksGreen300,
          backgroundColor: Colors.light.hereworksGreen50,
          textColor: Colors.light.hereworksGreen500,
        };
      }
      if (selectedTicket.status === TicketStatus.INPROGRESS
        || selectedTicket.status === TicketStatus.PAUSED) {
        return {
          borderColor: Colors.light.hereworksBlue500,
          backgroundColor: Colors.light.hereworksBlue50,
          textColor: Colors.light.hereworksBlue500,
        };
      }
      return {
        borderColor: Colors.light.hereworksBlue300,
        backgroundColor: Colors.light.plainBackground,
        textColor: Colors.light.hereworksBlack500,
      };
    };

    const getStatusLabel = (): string => {
      if (selectedTicket.status === TicketStatus.COMPLETED) return 'Resolved';
      if (selectedTicket.status === TicketStatus.INPROGRESS
        || selectedTicket.status === TicketStatus.PAUSED) return 'In Progress';
      return 'Open';
    };

    const statusTagStyles = getStatusTagStyles();

    const renderTicketDetailsWidget = () => <View style={styles.ticketDetailsContainer}>
      <Text style={[styles.sectionTitle, styles.ticketDetailsTitle]}>{'Ticket Details'}</Text>
      <View style={styles.ticketDetailsBody}>
        <View style={styles.ticketDetailsRow}>
          <View style={styles.sectionHeader}>
            <Text style={styles.propertyTitle}>{'ASSET'}</Text>
          </View>
          <View style={styles.ticketDetailsRowContent}>
            {canViewAssets ? <PressableStyled
              onPress={navigateToAsset}
              onHoverIn={() => setIsAssetLinkHovered(true)}
              onHoverOut={() => setIsAssetLinkHovered(false)}
              testID='ticket-navigate-to-asset-btn'
            >
              <Text
                style={[
                  styles.ticketLocation,
                  styles.assetLink,
                  isAssetLinkHovered && styles.assetLinkHovered,
                ]}
                selectable
              >
                {selectedTicket.assetName}
              </Text>
            </PressableStyled> : <Text
              style={[styles.ticketLocation, styles.ticketDetailsValue]} selectable>
              {selectedTicket.assetName}
            </Text>}
          </View>
        </View>
        <View style={styles.ticketDetailsRow}>
          <View style={styles.sectionHeader}>
            <Text style={styles.propertyTitle}>{'LOCATION'}</Text>
          </View>
          <View style={styles.ticketDetailsRowContent}>
            {isLocationSet ? <PressableStyled
              onPress={navigateToLocation}
              onHoverIn={() => setIsLocationLinkHovered(true)}
              onHoverOut={() => setIsLocationLinkHovered(false)}
              testID='ticket-navigate-to-location-btn'
            >
              <Text
                style={[
                  styles.ticketLocation,
                  styles.ticketDetailsValue,
                  styles.assetLink,
                  isLocationLinkHovered && styles.assetLinkHovered,
                ]}
                selectable
              >
                {locationParts.join(', ')}
              </Text>
            </PressableStyled> : <View style={styles.ticketDetailsRowContent}>
              <Text style={[styles.ticketLocation, styles.ticketDetailsValue]} selectable>
                {locationParts.join(', ')}
              </Text>
              <PressableStyled
                onPress={navigateToLocation}
                testID='ticket-navigate-to-location-btn'
                style={styles.locationButton}
              >
                <Text
                  style={[
                    styles.ticketLocation,
                    { color: Colors.light.hereworksBlue500, fontSize: 14 },
                  ]} selectable>
                  {'Set Location'}
                </Text>
              </PressableStyled>
            </View>}
          </View>
        </View>
        <View style={styles.ticketDetailsRow}>
          <View style={styles.sectionHeader}>
            <Text style={styles.propertyTitle}>{'REPORTED BY'}</Text>
          </View>
          <View style={styles.sectionHeader}>
            <TicketAssigneeIcon title={reporterName || 'Anonymous user'} />
            <Text style={[styles.ticketLocation, { marginLeft: 8 }]} selectable>
              {reporterName || 'Anonymous user'}
            </Text>
          </View>
        </View>
        {(canUpdateTickets && isDev) && <View
          style={[styles.ticketDetailsRow, { zIndex: 1 }]}
          testID='ticket-user-groups-field'
        >
          <View style={styles.sectionHeader}>
            <Text style={styles.propertyTitle}>{'USER GROUPS'}</Text>
          </View>
          <View style={{ flex: 1, alignItems: 'flex-start' }}>
            <TicketUserGroups
              {...{
                ticketId: selectedTicket.id,
                userGroups,
                ticketUserGroupIds: selectedTicket.userGroupIds,
                setSavingTicketUserGroups,
                onUpdateTicketUserGroups: (updatedTags: string[]) => {
                  updateSelectedTicketInList({
                    ...selectedTicket,
                    userGroupIds: updatedTags,
                  });
                },
                ticketResolved: selectedTicket.status === TicketStatus.COMPLETED,
              }}
            />
          </View>
          {savingTicketUserGroups && <View style={[StyleSheet.absoluteFill, {
            backgroundColor: `${Colors.light.plainBackground}80`,
            zIndex: 2,
            borderRadius: 12,
            justifyContent: 'center',
            alignItems: 'center',
          }]}>
            <ActivityIndicator size='small' color={Colors.light.hereworksBlue500} />
          </View>}
        </View>}
        {(canUpdateTickets || !!selectedTicket.assignee) && <View
          style={[styles.ticketDetailsRow, styles.ticketDetailsRowLast]}
          testID='assignee-field'
        >
          <View style={styles.sectionHeader}>
            <Text style={styles.propertyTitle}>{'ASSIGNEE'}</Text>
          </View>
          <TicketAssignee
            {...{
              userGroups,
              users,
              selectedTicket,
              showAssigneeDropdown,
              setShowAssigneeDropdown,
              assigneeSearchValue,
              setAssigneeSearchValue,
              setLoading,
              setShowSpinner,
              updateSelectedTicketInList,
            }}
          />
        </View>}
      </View>
    </View>;

    const renderSlaPolicyWidget = () => <View
      style={[styles.ticketDetailsContainer, styles.slaPolicyContainer]}
    >
      <Text style={[styles.sectionTitle, styles.ticketDetailsTitle]}>{'SLA Policy'}</Text>
      <View style={styles.ticketDetailsBody}>
        <View style={[styles.ticketDetailsRow]}>
          <Text style={styles.propertyTitle}>{'RESPONSE TIME'}</Text>
          <Text style={[styles.slaPolicyValue, { color: responseTimeColor }]} selectable>
            {responseTime}
          </Text>
        </View>
        <View style={[styles.ticketDetailsRow]}>
          <Text style={styles.propertyTitle}>{'RESOLUTION TIME'}</Text>
          <Text style={[styles.slaPolicyValue, { color: resolutionTimeColor }]} selectable>
            {resolutionTime}
          </Text>
        </View>
        <View style={[styles.ticketDetailsRow]}>
          <Text style={styles.propertyTitle}>{'TIME ON TASK'}</Text>
          <Text style={styles.slaPolicyValue} selectable>{timeOnTask}</Text>
        </View>
        <View style={[styles.ticketDetailsRow, styles.ticketDetailsRowLast]}>
          <Text style={styles.propertyTitle}>{'STARTED ON'}</Text>
          <Text style={styles.slaPolicyValue} selectable>{startedOn}</Text>
        </View>
      </View>
    </View>;

    const renderInfoAndActivity = () => <>
      <View style={styles.moreInformationSection}>
        <Text style={styles.ticketTitle} selectable>
          {selectedTicket.name.length > 50
            ? `${selectedTicket.name.slice(0, 49)}...` : selectedTicket.name}</Text>
        <Text style={styles.description}>{selectedTicket.issueDescription?.length
          ? selectedTicket.issueDescription : ''}</Text>
      </View>
      <View style={!isMobile && Style.row}>
        <View>
          {isMobile && <View style={styles.sendMessageSection}>
            <Text style={styles.reportedByText}>
              {`Reported ${reporterName ? `by ${reporterName}` : 'anonymously'}`}
            </Text>
            {<TicketAssigneeIcon title={reporterName || 'Anonymous user'} />}
          </View>}
        </View>
      </View>
      {moreOptionsVisible && <View style={styles.moreOptions}>
        {renderMoreOptions()}
      </View>}
      <ScrollView
        style={[styles.scrollview, !isMobile && styles.scrollviewDesktop]}
        showsVerticalScrollIndicator={false}
      >
        <Pressable disabled={!isMobile}>
          <View style={styles.statusSection}>
            <View>
              <View style={styles.statusActionsRow}>
                <View style={styles.prioritySelectorRowContainer}>
                  <TicketPrioritySelectorRow
                    {...{
                      id,
                      priority,
                      showPriorityDropdown,
                      setShowPriorityDropdown,
                      setLoading,
                      setShowSpinner,
                      updateSelectedTicketInList,
                    }}
                  />
                </View>
                {canUpdateTickets && <TicketStatusButtonsRow
                  {...{
                    selectedTicket,
                    updateSelectedTicketInList,
                    showSpinner,
                    setShowSpinner,
                    showAlert,
                    setLoading,
                    setClosureModalVisible,
                  }}
                />}
              </View>
              <TicketBrief ticketId={selectedTicket.id} />
              <View style={[isMobile && styles.detailSection]}>
                {(isMobile && canUpdateTickets && isDev) && <View
                  style={[
                    !isMobile && Style.row,
                    styles.assigneeSection,
                    {
                      paddingVertical: 8,
                      paddingHorizontal: isMobile ? 10 : 0,
                      zIndex: 1,
                    },
                  ]}
                  testID='assignee-field'>
                  <View style={styles.sectionHeader}>
                    <MyGroupsIcon color={Colors.light.hereworksBlack400} />
                    <Text style={styles.propertyTitle}>{'User groups'}</Text>
                  </View>
                  <View style={{ flex: 1, alignItems: 'flex-end' }}>
                    <TicketUserGroups
                      {...{
                        ticketId: selectedTicket.id,
                        userGroups,
                        ticketUserGroupIds: selectedTicket.userGroupIds,
                        setSavingTicketUserGroups,
                        onUpdateTicketUserGroups: (updatedTags: string[]) => {
                          updateSelectedTicketInList({
                            ...selectedTicket,
                            userGroupIds: updatedTags,
                          });
                        },
                        ticketResolved: selectedTicket.status === TicketStatus.COMPLETED,
                      }}
                    />
                  </View>
                  {savingTicketUserGroups && <View style={[StyleSheet.absoluteFill, {
                    backgroundColor: `${Colors.light.plainBackground}80`,
                    zIndex: 2,
                    borderRadius: 12,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }]}>
                    <ActivityIndicator size='small' color={Colors.light.hereworksBlue500} />
                  </View>}
                </View>}
                {(isMobile && (canUpdateTickets || !!selectedTicket.assignee)) && <View
                  style={[
                    !isMobile && Style.row, styles.detailSubsection, styles.assigneeSection,
                  ]}
                  testID='assignee-field'>
                  <View style={styles.sectionHeader}>
                    <ProfileIcon color={Colors.light.hereworksBlack400} />
                    <Text style={styles.propertyTitle}>{'Assignee'}</Text>
                  </View>
                  <TicketAssignee
                    {...{
                      userGroups,
                      users,
                      selectedTicket,
                      showAssigneeDropdown,
                      setShowAssigneeDropdown,
                      assigneeSearchValue,
                      setAssigneeSearchValue,
                      setLoading,
                      setShowSpinner,
                      updateSelectedTicketInList,
                    }}
                  />
                </View>}
              </View>
            </View>
          </View>
          {selectedTicket.images?.length ? <>
            <Modal
              isVisible={imageModalVisible}
              animationIn={'fadeIn'}
              animationOut={'fadeOut'}
            >
              <Pressable
                onPress={() => setImageModalVisible(!imageModalVisible)}
                style={{ height: '100%', flex: 1 }}
              >
                <Image
                  source={{ uri }}
                  resizeMode={'contain'}
                  style={{ width: undefined, height: '100%' }}
                />
              </Pressable>
            </Modal>
            <PressableStyled
              onPress={() => setImageModalVisible(!imageModalVisible)}
              testID='ticket-open-picture-btn'
              style={styles.imageContainer}
            >
              <Image
                source={{ uri }}
                resizeMode={'cover'}
                style={styles.image}
              />
              <ZoomIcon
                style={{
                  position: 'absolute',
                  top: isMobile ? undefined : 16,
                  right: 16,
                  borderRadius: 3,
                  backgroundColor: `${Colors.light.hereworksBlack300}05`,
                }}
              />
            </PressableStyled>
          </> : <></>}
          {isMobile && renderSlaPolicyWidget()}
          <SendMessageModal
            {...{
              updateSelectedTicketInList, messageModalShown, id,
            }}
            hide={() => setMessageModalShown(false)}
          />
          <ClosureModal
            id={selectedTicket.id}
            {...{ resolve }}
            hide={() => setClosureModalVisible(false)}
            isVisible={closureModalVisible}
          />
          <Modal
            isVisible={activityLogModalVisible}
            onBackButtonPress={() => setActivityLogModalVisible(false)}
            onBackdropPress={() => setActivityLogModalVisible(false)}
            style={Style.modal}
          >
            <View style={[Style.modalContent, styles.activityLogModal]}>
              <View style={styles.activityLogModalHeader}>
                <Text style={styles.activityLogModalTitle}>{'Activity Log'}</Text>
                <PressableStyled
                  onPress={() => setActivityLogModalVisible(false)}
                  style={styles.closeSelectedTicketButton}
                  testID='activity-log-close-btn'
                >
                  <CloseIcon16 color={Colors.light.hereworksBlue200} />
                </PressableStyled>
              </View>
              <ScrollView
                showsVerticalScrollIndicator={false}
                style={styles.activityLogModalBody}
              >
                <TicketActivity
                  {...{ selectedTicket, users }}
                  sectionSelected={ActivitySection.HISTORY}
                  hideTabs
                />
              </ScrollView>
            </View>
          </Modal>
          <TicketActivity
            {...{ selectedTicket, users }}
            ref={ticketActivityRef}
            sectionSelected={activeTab}
            setSectionSelected={setActiveTab}
          />
        </Pressable>
        {(canUpdateTickets
          && activeTab !== ActivitySection.REPORTER)
          && <CommentsPane {...{ name, updateSelectedTicketInList, id }} />}
        {(canUpdateTickets
          && activeTab === ActivitySection.REPORTER)
          && <MessageReporterPane
            {...{ name, updateSelectedTicketInList, id }}
          />}
      </ScrollView>
    </>;

    return <KeyboardAvoidingView
      style={{ flex: 1 }}
      behavior='padding'
      testID='ticket-details'
    >
      <ScrollView
        showsVerticalScrollIndicator={false}
        nestedScrollEnabled
        ref={scrollRef}>
        <Animatable.View
          style={[styles.container, isMobile && styles.containerMobile]}
          animation={'fadeIn'}
        >
          {renderSharePane()}
          <View style={styles.ticketMetaHeader}>
            <View style={[styles.statusTag, {
              borderColor: statusTagStyles.borderColor,
              backgroundColor: statusTagStyles.backgroundColor,
            }]}>
              <Text style={[styles.statusTagText, {
                color: statusTagStyles.textColor,
              }]}>{getStatusLabel()}</Text>
            </View>
            <View style={styles.ticketMetaGroup}>
              <PressableStyled
                onPress={copyTicketLink}
                onHoverIn={() => setIsTicketLinkHovered(true)}
                onHoverOut={() => setIsTicketLinkHovered(false)}
                style={styles.ticketIdLinkButton}
                testID='ticket-copy-link-btn'
              >
                <Text
                  style={[
                    styles.ticketMetaText,
                    styles.ticketIdLink,
                    isTicketLinkHovered && styles.ticketIdLinkHovered,
                  ]}
                >
                  {`ID: ${selectedTicket.id}`}
                </Text>
                {isTicketLinkHovered && <LinkChainIcon24 />}
              </PressableStyled>
              <View style={styles.ticketMetaActions}>
                <Text style={styles.ticketTimeText}>{`Created on: ${createdOn}`}</Text>
                <PressableStyled
                  onPress={() => setShowHeaderMenu((previous) => !previous)}
                  style={styles.closeSelectedTicketButton}
                  testID='selected-ticket-menu-btn'
                >
                  <EllipsisIcon fill={Colors.light.hereworksBlue200} />
                </PressableStyled>
                <PressableStyled
                  onPress={() => setSelectedTicket(null)}
                  style={styles.closeSelectedTicketButton}
                  testID='selected-ticket-close-btn'
                >
                  <CloseIcon16 color={Colors.light.hereworksBlue200} />
                </PressableStyled>
              </View>
            </View>
          </View>
          {showHeaderMenu && <View style={styles.headerDropdown}>
            <PressableStyled
              onPress={shareFromHeaderMenu}
              style={styles.dropdownButton}
              testID='selected-ticket-share-btn'
            >
              <Text style={styles.dropdownText}>{'Share'}</Text>
            </PressableStyled>
            <PressableStyled
              onPress={showActivityLogFromHeaderMenu}
              style={styles.dropdownButton}
              testID='selected-ticket-activity-log-btn'
            >
              <Text style={styles.dropdownText}>{'Activity Log'}</Text>
            </PressableStyled>
            {canDeleteTickets && <PressableStyled
              onPress={deleteFromHeaderMenu}
              style={styles.dropdownButton}
              testID='selected-ticket-delete-btn'
            >
              <Text style={styles.dropdownText}>{'Delete'}</Text>
            </PressableStyled>}
          </View>}
          <View style={styles.subContainer}>
            <View style={!isMobile && styles.subContainerDesktop}>
              {isMobile && <View style={styles.completedTicketHeader}>
                <Pressable style={{ flex: 1, alignSelf: 'stretch' }} disabled={!isMobile}>
                  <Pressable disabled={!isMobile}>
                    {isMobile && <>
                      <Text style={styles.sectionTitle}>{'Asset'}</Text>
                      <View style={[styles.detailSection, { padding: 10 }]}>
                        <View style={Style.row}>
                          <AssetIcon style={{ marginRight: 8 }} />
                          {canViewAssets ? <PressableStyled
                            onPress={navigateToAsset}
                            onHoverIn={() => setIsAssetLinkHovered(true)}
                            onHoverOut={() => setIsAssetLinkHovered(false)}
                            testID='ticket-navigate-to-asset-btn'
                            style={{ flex: 1 }}
                          >
                            <Text
                              style={[
                                styles.ticketLocation,
                                styles.assetLink,
                                isAssetLinkHovered && styles.assetLinkHovered,
                              ]}
                              selectable
                            >
                              {`${selectedTicket.assetName}`}
                            </Text>
                          </PressableStyled> : <Text
                            style={[styles.ticketLocation, { flex: 1 }]} selectable>
                            {`${selectedTicket.assetName}`}
                          </Text>}
                        </View>
                      </View>
                      <Text style={styles.sectionTitle}>{'Location'}</Text>
                      <Pressable
                        style={Style.row}
                        disabled={!isMobile}
                      >
                        {isLocationSet ? <PressableStyled
                          onPress={navigateToLocation}
                          testID='ticket-navigate-to-location-btn'
                          onHoverIn={() => setIsLocationLinkHovered(true)}
                          onHoverOut={() => setIsLocationLinkHovered(false)}
                          style={{ flex: 1 }}
                        >
                          <Text
                            style={[
                              styles.ticketLocation,
                              styles.assetLink,
                              isLocationLinkHovered && styles.assetLinkHovered,
                            ]} selectable>
                            {locationParts.join(', ')}
                          </Text>
                        </PressableStyled> : <>
                          <Text style={styles.ticketLocation}>
                            {locationParts.join(', ')}
                          </Text>
                          <PressableStyled
                            onPress={navigateToLocation}
                            testID='ticket-navigate-to-location-btn'
                            style={styles.locationButton}
                          >
                            <Text
                              style={[
                                styles.ticketLocation,
                                { color: Colors.light.hereworksBlue500, fontSize: 14 },
                              ]} selectable>
                              {'Set Location'}
                            </Text>
                          </PressableStyled>
                        </>}
                      </Pressable>
                    </>}
                  </Pressable>
                  {isMobile && <View style={{ marginBottom: 12 }}>
                    <View>
                      <Text style={styles.sectionTitle}>{selectedTicket.name}</Text>
                      <Text style={styles.description}>{selectedTicket.issueDescription?.length
                        ? selectedTicket.issueDescription : selectedTicket.name}</Text>
                    </View>
                  </View>}
                </Pressable>
              </View>}
              {!isMobile && <View style={styles.leftContentSection}>
                {renderInfoAndActivity()}
              </View>}
              {!isMobile && <View style={styles.ticketDetailsColumn}>
                {renderTicketDetailsWidget()}
                {renderSlaPolicyWidget()}
              </View>}
            </View>
            {isMobile && renderInfoAndActivity()}
          </View>
          <ActivityIndicator
            animating={showSpinner}
            size='large'
            style={Style.activityIndicator}
          />
        </Animatable.View>
      </ScrollView>
    </KeyboardAvoidingView>;
  }

  return <ScreenEmptyState
    image={<NoSelectedTicketImage />}
    title='Choose ticket'
    subtitle='Choose the ticket you want to work with'
  />;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.light.plainBackground,
    borderRadius: 12,
  },
  containerMobile: {
    margin: 20,
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite200,
  },
  ticketMetaHeader: {
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: isMobile ? 'flex-start' : 'center',
    gap: 8,
    paddingHorizontal: isMobile ? 20 : 24,
    paddingTop: isMobile ? 12 : 16,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksBlue50,
  },
  ticketMetaGroup: {
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: isMobile ? 'flex-start' : 'center',
    gap: isMobile ? 2 : 12,
    flex: 1,
  },
  ticketMetaActions: {
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  headerDropdown: {
    position: 'absolute',
    top: isMobile ? 62 : 54,
    right: isMobile ? 20 : 24,
    backgroundColor: Colors.light.plainBackground,
    borderColor: Colors.light.hereworksBlue100,
    borderWidth: 1.5,
    borderRadius: 12,
    zIndex: 5,
  },
  dropdownButton: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    alignItems: 'flex-start',
    minWidth: 96,
  },
  dropdownText: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksPrimaryGrey,
  },
  activityLogModal: {
    alignSelf: 'center',
    width: isMobile ? '92%' : 560,
    maxHeight: isMobile ? '82%' : '78%',
    borderRadius: 8,
    padding: 20,
  },
  activityLogModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  activityLogModalTitle: {
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 18,
    lineHeight: 24,
    color: Colors.light.hereworksBlack500,
  },
  activityLogModalBody: {
    flexGrow: 0,
  },
  ticketIdLinkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  ticketIdLink: {
    color: Colors.light.hereworksBlack400,
  },
  ticketIdLinkHovered: {
    textDecorationLine: 'underline',
  },
  ticketMetaText: {
    fontSize: 12,
    lineHeight: 18,
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Poppins_400Regular',
  },
  ticketTimeText: {
    fontSize: 14,
    lineHeight: 18,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Roboto_400Regular',
  },
  closeSelectedTicketButton: {
    width: 24,
    height: 24,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.light.plainBackground,
  },
  statusTag: {
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 4,
    paddingVertical: 1,
    paddingHorizontal: 8,
  },
  statusTagText: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
    lineHeight: 20,
  },
  subContainer: {
    paddingHorizontal: isMobile ? 14 : 12,
  },
  subContainerDesktop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  subContainerMain: {
    flex: 1,
  },
  ticketDetailsColumn: {
    width: 360,
    padding: 16,
    marginStart: 16,
    backgroundColor: Colors.light.hereworksWhite100,
  },
  leftContentSection: {
    flex: 1,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingBottom: 10,
    backgroundColor: Colors.light.plainBackground,
  },
  moreInformationSection: {
    marginBottom: 8,
  },
  ticketDetailsContainer: {
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingBottom: 10,
    backgroundColor: Colors.light.plainBackground,
    zIndex: 10,
  },
  slaPolicyContainer: {
    marginTop: 12,
    zIndex: 9,
  },
  slaPolicyValue: {
    fontSize: 14,
    lineHeight: 18,
    color: Colors.light.hereworksBlack500,
    fontFamily: 'Roboto_400Regular',
    flex: 1,
  },
  ticketDetailsTitle: {
    marginBottom: 10,
  },
  ticketDetailsBody: {
  },
  ticketDetailsRow: {
    paddingVertical: 10,
  },
  ticketDetailsRowLast: {
    borderBottomWidth: 0,
  },
  ticketDetailsRowContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  ticketDetailsValue: {
    flex: 1,
    marginEnd: 8,
  },
  assetLink: {
    color: Colors.light.hereworksBlue500,
  },
  assetLinkHovered: {
    textDecorationLine: 'underline',
  },
  completedTicketHeader: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  ticketTitle: {
    fontFamily: 'Poppins_500Medium',
    fontSize: isMobile ? 16 : 22,
    lineHeight: isMobile ? 24 : 33,
    color: Colors.light.hereworksBlack600,
    paddingHorizontal: isMobile ? 20 : 0,
    paddingVertical: isMobile ? 12 : 16,
  },
  ticketLocation: {
    fontSize: isMobile ? 14 : 16,
    lineHeight: isMobile ? 18 : 21,
    color: Colors.light.hereworksBlack500,
    fontFamily: 'Roboto_400Regular',
    marginVertical: 2,
    marginEnd: 4,
  },
  moreOptions: {
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 2,
  },
  ticketDetail: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    lineHeight: 21,
    color: Colors.light.hereworksPrimaryGrey900,
  },
  descriptionTitle: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    lineHeight: 21,
    color: Colors.light.hereworksBlack400,
  },
  description: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    lineHeight: 21,
    color: Colors.light.hereworksBlack500,
  },
  imageContainer: {
    marginTop: 16,
    justifyContent: 'center',
  },
  image: {
    borderRadius: 8,
    backgroundColor: Colors.light.hereworksPrimaryGrey,
    width: '100%',
    height: isMobile ? 100 : 150,
  },
  sendMessageTitle: {
    marginStart: 8,
    fontSize: 14,
    lineHeight: 18,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlue400,
  },
  propertyTitle: {
    fontSize: 10,
    lineHeight: 16,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack400,
  },
  detailSection: {
    paddingHorizontal: isMobile ? undefined : 12,
    paddingVertical: isMobile ? undefined : 8,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    alignSelf: 'stretch',
  },
  sendMessageSection: {
    flexDirection: 'row',
    alignSelf: isMobile ? undefined : 'flex-end',
    paddingTop: 8,
  },
  reportedByText: {
    fontSize: isMobile ? 14 : 16,
    lineHeight: isMobile ? 18 : 21,
    fontFamily: isMobile ? 'Roboto_400Regular' : 'Poppins_500Medium',
    color: Colors.light.hereworksBlack400,
    marginHorizontal: isMobile ? undefined : 8,
    flex: 1,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: isMobile ? 8 : 0,
  },
  detailSubsection: isMobile ? {
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksBlue100,
    paddingHorizontal: 12,
  } : {
    height: 40,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksBlue50,
  },
  reporter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  assigneeFullName: {
    marginStart: 8,
  },
  statusSection: {
    flexDirection: isMobile ? 'column-reverse' : 'column',
    zIndex: 2,
  },
  statusActionsRow: {
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: isMobile ? 'flex-start' : 'center',
    marginBottom: 16,
    gap: 10,
    zIndex: 3,
  },
  prioritySelectorRowContainer: {
    position: 'relative',
    zIndex: 4,
  },
  scrollview: {
    flex: 1,
    paddingHorizontal: isMobile ? 14 : 24,
    marginHorizontal: 0,
    marginTop: 8,
  },
  scrollviewDesktop: {
    paddingHorizontal: 0,
  },
  prioritySection: {
    zIndex: 2,
  },
  assigneeSection: {
    borderBottomWidth: undefined,
  },
  sectionTitle: {
    marginTop: 8,
    marginBottom: isMobile ? 12 : 8,
    fontSize: isMobile ? 16 : 14,
    lineHeight: isMobile ? 24 : 27,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack500,
  },
  locationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 4,
    borderColor: Colors.light.hereworksBlue500,
    paddingHorizontal: 6,
  },
});

export default SelectedTicket;
